import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { OrgDocument } from '@/lib/partnerTypes';

interface Props {
  orgId: string;
  canEdit: boolean;
  onCountChange?: (count: number) => void;
}

const FILE_ICONS: Record<string, { icon: string; color: string }> = {
  'application/pdf': { icon: 'ri-file-pdf-2-line', color: 'text-red-500' },
  'application/msword': { icon: 'ri-file-word-line', color: 'text-[#1a2e4a]' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: 'ri-file-word-line', color: 'text-[#1a2e4a]' },
  'application/vnd.ms-excel': { icon: 'ri-file-excel-2-line', color: 'text-emerald-600' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: 'ri-file-excel-2-line', color: 'text-emerald-600' },
  'image/jpeg': { icon: 'ri-image-2-line', color: 'text-amber-500' },
  'image/png': { icon: 'ri-image-2-line', color: 'text-amber-500' },
  'application/zip': { icon: 'ri-file-zip-line', color: 'text-stone-500' },
};

function getFileIcon(type: string | null) {
  if (!type) return { icon: 'ri-file-line', color: 'text-stone-400' };
  return FILE_ICONS[type] ?? { icon: 'ri-file-line', color: 'text-stone-400' };
}

/** Extract storage path from a Supabase public URL or return null */
function extractStoragePath(url: string): string | null {
  const marker = '/org-documents/';
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
}

export default function DocumentsTab({ orgId, canEdit, onCountChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [docs, setDocs] = useState<OrgDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('org_documents')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });
    setDocs(data ?? []);
    setLoading(false);
    onCountChange?.((data ?? []).length);
  }, [orgId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      setError('File too large. Max 20 MB.');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(`Uploading ${file.name}...`);

    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `${orgId}/${Date.now()}_${safeName}`;

    const { error: uploadErr } = await supabase.storage
      .from('org-documents')
      .upload(storagePath, file, { contentType: file.type });

    if (uploadErr) {
      setError(uploadErr.message);
      setUploading(false);
      setUploadProgress('');
      return;
    }

    // Store storage_path; url field kept for backward compat (empty string)
    const { error: insertErr } = await supabase.from('org_documents').insert({
      organization_id: orgId,
      name: file.name,
      url: '',
      storage_path: storagePath,
      file_type: file.type || null,
    });

    if (insertErr) {
      setError(insertErr.message);
    } else {
      showToast(`"${file.name}" uploaded successfully.`);
      fetchDocs();
    }

    setUploading(false);
    setUploadProgress('');
    if (inputRef.current) inputRef.current.value = '';
  };

  /** Resolve download: prefer storage_path, fallback to extracting from url */
  const getPath = (doc: OrgDocument): string | null => {
    if (doc.storage_path) return doc.storage_path;
    if (doc.url) return extractStoragePath(doc.url);
    return null;
  };

  const handleDownload = async (doc: OrgDocument) => {
    const path = getPath(doc);
    if (!path) { setError('Storage path not found for this document.'); return; }

    setDownloading(doc.id);
    const { data, error: signErr } = await supabase.storage
      .from('org-documents')
      .createSignedUrl(path, 3600); // 1 hour

    setDownloading(null);

    if (signErr || !data?.signedUrl) {
      setError(signErr?.message ?? 'Could not generate download link.');
      return;
    }

    // Trigger download
    const a = document.createElement('a');
    a.href = data.signedUrl;
    a.download = doc.name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async (doc: OrgDocument) => {
    setDeleting(doc.id);
    const path = getPath(doc);
    if (path) {
      await supabase.storage.from('org-documents').remove([path]);
    }
    const { error: delErr } = await supabase.from('org_documents').delete().eq('id', doc.id);
    if (delErr) {
      setError(delErr.message);
    } else {
      showToast('Document deleted.');
      setDocs(prev => {
        const next = prev.filter(d => d.id !== doc.id);
        onCountChange?.(next.length);
        return next;
      });
    }
    setDeleting(null);
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg">
          <i className="ri-check-line mr-2" />{toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-bold text-[#1a2e4a]">Documents ({docs.length})</h3>
          <p className="text-xs text-stone-400 mt-0.5">PDF, Word, Excel and other files — max 20 MB each</p>
        </div>
        {canEdit && (
          <>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 bg-[#1a2e4a] text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-[#253f62] disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
            >
              {uploading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-upload-2-line" />}
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.png,.jpg,.jpeg"
            />
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm text-red-600">
          <i className="ri-error-warning-line" />
          {error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line" /></button>
        </div>
      )}

      {uploadProgress && (
        <div className="mb-4 bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm text-stone-600">
          <i className="ri-loader-4-line animate-spin" />
          {uploadProgress}
        </div>
      )}

      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <i className="ri-loader-4-line text-xl text-stone-300 animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div className="py-12 text-center border-2 border-dashed border-stone-200 rounded-xl">
          <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
            <i className="ri-folder-open-line text-stone-400 text-xl" />
          </div>
          <p className="text-sm font-medium text-stone-500">No documents yet</p>
          {canEdit && (
            <p className="text-xs text-stone-400 mt-1">
              Click <strong>Upload File</strong> to add the first document
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => {
            const { icon, color } = getFileIcon(doc.file_type);
            const ext = doc.name.split('.').pop()?.toUpperCase() ?? '';
            const isDownloading = downloading === doc.id;
            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 px-4 py-3 bg-stone-50 border border-stone-100 rounded-xl hover:border-stone-200 transition-colors group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                  <i className={`${icon} ${color} text-lg`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1a2e4a] truncate leading-tight">{doc.name}</p>
                  <p className="text-xs text-stone-400 mt-0.5">
                    {ext && <span className="font-medium text-stone-500 mr-1.5">{ext}</span>}
                    {new Date(doc.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleDownload(doc)}
                    disabled={isDownloading}
                    className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-[#1a2e4a] hover:bg-white rounded-lg transition-colors cursor-pointer disabled:opacity-60"
                    title="Download"
                  >
                    {isDownloading
                      ? <i className="ri-loader-4-line animate-spin text-sm" />
                      : <i className="ri-download-2-line text-sm" />
                    }
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => handleDelete(doc)}
                      disabled={deleting === doc.id}
                      className="w-8 h-8 flex items-center justify-center text-stone-300 hover:text-red-500 hover:bg-white rounded-lg transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                      title="Delete"
                    >
                      {deleting === doc.id
                        ? <i className="ri-loader-4-line animate-spin text-sm" />
                        : <i className="ri-delete-bin-line text-sm" />
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
